import {
  INTERP_DELAY_MS,
  TICK_RATE,
  clampPitch,
  eyeHeightFor,
  fittedMagazine,
  isWeaponBroken,
  loadedRounds,
  sightZeroElevationRad,
  wearExtraSpreadDeg,
  weaponComplete,
  type EffectiveWeapon,
  type Snapshot,
  type Vec3,
} from "@rietz/shared";
import type { NetClock } from "../net/clock.js";
import type { Prediction } from "../net/prediction.js";
import type { GameSocket } from "../net/socket.js";
import type { Input } from "../input/input.js";
import type { Viewmodel } from "../render/viewmodel.js";
import type { AimSystem } from "./aim.js";
import type { VisualBullets } from "./bullets.js";
import type { Fx } from "./fx.js";
import type { InventoryController } from "./inventory.js";
import { applyLeanOffset } from "./lean.js";
import type { GameAudio } from "../audio/synth.js";
import { pulseHitMarker, type HitMarkerKind } from "../ui/store.js";

/**
 * The local weapon: trigger, ammunition, and everything a shot kicks off. The
 * client is not authoritative — everything here is a prediction the server may
 * contradict — but the trigger must respond on the frame, the counter tick
 * down instantly, the tracer leave before the packet.
 *
 * AMMUNITION: displayed = `serverMag − pending shots`. A pending shot is
 * retired when a snapshot shows the magazine going DOWN (nothing but an
 * accepted shot decrements it outside a reload); an age cutoff survives for a
 * shot the server rejected. `magKnown` is a separate DISPLAY question — every
 * shot forgets the count, a `magCheck` learns it back, the HUD draws "?".
 *
 * CONDITION: the trigger refuses a broken gun as `acceptFire` does, and every
 * shot leaves along a direction wear has widened. Neither is predicted — wear
 * arrives on the throttled `inv` resync (`isBroken`).
 *
 * HIT FEEDBACK: `predicted` (our own simulated round reached a drawn pose —
 * faint, occasionally wrong), `hit` (a `hit` event with us as shooter), `kill`.
 *
 * OFFLINE: everything local runs, nothing is sent, so no hit resolution and
 * reloads never complete. Every number comes from the `EffectiveWeapon` the
 * inventory controller derives, recomputed on weapon change, never per frame.
 * Empty hands are a real state: buffered pulls are discarded.
 */

/**
 * Extra grace over the round trip before a pending shot is assumed reflected in
 * the server's count: one snapshot interval plus jitter.
 */
const ACK_GRACE_MS = 120;

/** Ticks of interpolation delay — what `renderTick` is offset by. */
const INTERP_DELAY_TICKS = (INTERP_DELAY_MS / 1000) * TICK_RATE;

/**
 * Buckshot half-angle, mirroring the server's 2.5°. Cosmetic — the server rolls
 * its own pellets — but the SHAPE must match or the player learns the wrong
 * gun.
 */
const PELLET_CONE_RAD = (2.5 * Math.PI) / 180;

/** A key `InventoryController.weaponKey` can never produce (it is either empty
 *  or contains an iid), so the first sync always runs. */
const NO_WEAPON_KEY = " ";

export interface FireControlOptions {
  input: Input;
  prediction: Prediction;
  clock: NetClock;
  viewmodel: Viewmodel;
  /**
   * Recoil and sway as two angles — the SAME two the loop poses the camera
   * with, which is what makes recoil a mechanic and not an animation.
   */
  aim: AimSystem;
  fx: Fx;
  audio: GameAudio;
  /** `null` offline: everything runs locally, nothing is sent. */
  socket: GameSocket | null;
  /** Where a fired round goes to be flown, drawn, and — if it connects with a
   *  drawn pose — turned into the predicted marker. */
  bullets: VisualBullets;
  /** Where the gun comes from: what is equipped, and what is loaded in it. */
  inventory: InventoryController;
}

export class FireControl {
  private readonly o: FireControlOptions;

  /** Authoritative magazine from the last snapshot. */
  private serverMag = 0;
  /** The value before it, so a magazine going UP can be spotted as a reload. */
  private prevServerMag = 0;
  /** `performance.now()` of each local shot the server may not have seen yet. */
  private readonly pending: number[] = [];

  private reloading = false;
  private dead = false;
  /** A magazine check is in the hands, straight off `YouSnap.weapon.checking`. */
  private checking = false;
  /**
   * The server's `YouSnap.weapon.unknown` — its half of the answer; `magKnown`
   * folds predicted shots in.
   */
  private serverUnknown = false;

  /** `performance.now()` of the last accepted trigger pull. */
  private lastShotAtMs = -Infinity;

  // --- The equipped weapon, resolved once per weapon change ----------------
  private weaponKey = NO_WEAPON_KEY;
  private eff: EffectiveWeapon | null = null;
  private fireIntervalMs = Infinity;
  private magSizeCache = 0;
  private hipSpreadRad = 0;
  private zeroElevationRad = 0;
  private pellets = 1;
  private autoFire = false;
  /** Item def id, for the per-weapon shot sound. */
  private weaponSoundId = "";
  /** A can is fitted: the local report is quieter and duller. Never public —
   *  remotes hear an unsuppressed shot, because the wire carries no mod list. */
  private suppressed = false;
  /**
   * Every vital slot on the held gun is filled (`weaponComplete`, the predicate
   * `acceptFire` refuses on). True with no gun; false only on one taken apart
   * at the bench.
   */
  private complete = true;

  /** Scratch — the fire path runs inside the render loop and must not allocate. */
  private readonly eye: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly dir: Vec3 = { x: 0, y: 0, z: 0 };
  /** One pellet's deviated direction, rewritten per pellet per shot. */
  private readonly pellet: Vec3 = { x: 0, y: 0, z: 0 };

  constructor(opts: FireControlOptions) {
    this.o = opts;
  }

  /** Rounds to display. Never negative, never above the magazine size. */
  get mag(): number {
    const size = this.magSizeCache;
    const predicted = this.serverMag - this.pending.length;
    return predicted < 0 ? 0 : predicted > size ? size : predicted;
  }

  /** Magazine capacity of the equipped weapon; 0 when empty-handed. */
  get magSize(): number {
    return this.magSizeCache;
  }

  get isReloading(): boolean {
    return this.reloading;
  }

  /** A magazine check is running. Drives the viewmodel's glance and the HUD's
   *  "Checking…", the way `isReloading` drives the reload's. */
  get isChecking(): boolean {
    return this.checking;
  }

  /**
   * Whether the HUD's count is one the player KNOWS; false draws "?". Two
   * sources: the server's `unknown` (half an RTT late) and a PREDICTED shot,
   * which forgets the count the moment the round leaves. A refused shot ages
   * out of `pending` and the answer falls back to the server's.
   */
  get magKnown(): boolean {
    return !this.serverUnknown && this.pending.length === 0;
  }

  /** Reconciles the predicted magazine against the server's. */
  onSnapshot(snap: Snapshot, nowMs: number): void {
    // The `inv` that changed the weapon may have arrived in the same batch as
    // this snapshot; syncing first means the clamp below uses the right size.
    this.syncWeapon();
    this.prevServerMag = this.serverMag;
    this.serverMag = snap.you.weapon.mag;
    this.reloading = snap.you.weapon.reloading;
    // Both omitted when false on the wire, so absent reads as "known, idle".
    this.checking = snap.you.weapon.checking === true;
    this.serverUnknown = snap.you.weapon.unknown === true;
    this.dead = snap.you.dead;

    // A magazine that went UP means a reload landed (or a respawn), which
    // invalidates every outstanding shot. Same for a reload in progress.
    if (this.serverMag > this.prevServerMag || this.reloading) {
      this.pending.length = 0;
      return;
    }

    // Each decrement is one accepted shot: retire that many pending, oldest
    // first, or they are double-counted until the age cutoff.
    const fired = this.prevServerMag - this.serverMag;
    if (fired > 0) this.pending.splice(0, fired);

    // Age fallback, for shots the server rejected and will never decrement
    // for — see the ammunition note at the top of the file.
    const cutoff = nowMs - (this.o.clock.rttMs + ACK_GRACE_MS);
    let drop = 0;
    while (drop < this.pending.length && (this.pending[drop] as number) <= cutoff) {
      drop += 1;
    }
    if (drop > 0) this.pending.splice(0, drop);
  }

  /** Death or respawn arriving out of band (a `death` event about us). */
  setDead(dead: boolean): void {
    this.dead = dead;
    if (dead) this.pending.length = 0;
  }

  /**
   * One frame's worth of trigger — per frame, since that is when the finger
   * moves and the aimed camera angle exists.
   */
  update(nowMs: number): void {
    this.syncWeapon();

    // Drained unconditionally: a pull that cannot be honoured is stale intent,
    // not a queued instruction.
    const presses = this.o.input.consumeFirePresses();
    if (this.eff === null || this.dead) return;

    if (this.autoFire) {
      // Full auto: the rpm gate paces a held button; the buffered press still
      // counts for a click inside a single frame.
      if (this.o.input.fireHeld || presses > 0) {
        if (this.canFire(nowMs)) this.fire(nowMs);
      }
      return;
    }

    let remaining = presses;
    while (remaining > 0) {
      remaining -= 1;
      if (!this.canFire(nowMs)) return;
      this.fire(nowMs);
    }
  }

  /**
   * The V key: ask the server to look into the magazine. Nothing is predicted
   * — the server's `checking` flag drives the viewmodel as `reloading` does.
   * The refusals below mirror the server's so a hopeless press never becomes a
   * packet (an empty well is the interesting one — `loadedRoundsKnown` already
   * answers "known"). Not gated on `canFire`: the trigger stays live through a check.
   */
  magCheck(): void {
    if (this.dead) return;
    if (this.reloading) return;
    if (this.checking) return;
    const def = this.o.inventory.weaponDef;
    if (def === null) return;
    if (fittedMagazine(def, this.o.inventory.weaponInstance) === null) return;
    this.o.socket?.sendMagCheck();
  }

  // -----------------------------------------------------------------------

  /**
   * Re-derives everything that depends on WHICH gun is in hand. One string
   * compare per frame; expensive inside.
   */
  private syncWeapon(): void {
    const inv = this.o.inventory;
    const key = inv.weaponKey;
    if (key === this.weaponKey) return;
    this.weaponKey = key;

    const eff = inv.effectiveWeapon;
    const def = inv.weaponDef;
    this.eff = eff;

    if (eff === null || def === null) {
      this.fireIntervalMs = Infinity;
      this.magSizeCache = 0;
      this.hipSpreadRad = 0;
      this.zeroElevationRad = 0;
      this.pellets = 1;
      this.autoFire = false;
      this.weaponSoundId = "";
      this.suppressed = false;
      this.complete = true;
      this.o.viewmodel.setWeapon(null, { adsTimeSec: 0 });
      this.o.aim.setWeapon(null);
    } else {
      this.fireIntervalMs = 60000 / eff.rpm;
      this.magSizeCache = eff.magSize;
      this.hipSpreadRad = (eff.hipSpreadDeg * Math.PI) / 180;
      // Depends on muzzle velocity, which the cartridge and a suppressor scale
      // — why both are in the weapon key.
      this.zeroElevationRad = sightZeroElevationRad(eff);
      this.pellets = eff.pellets;
      this.autoFire = def.fireMode === "auto";
      this.weaponSoundId = def.id;
      this.suppressed = inv.weaponSuppressed;
      this.complete = weaponComplete(def, inv.weaponInstance);
      // The kick scale, folded once for both consumers so the drawn recoil and
      // the thrown view can never differ. `def.recoil` is the gun,
      // `weaponRecoilMult` the parts.
      const recoilMult = def.recoil * inv.weaponRecoilMult;
      // The aggregated ADS time, or a scope would slow the readout but not the
      // animation.
      this.o.viewmodel.setWeapon(def.id, {
        adsTimeSec: eff.adsTimeSec,
        mods: inv.weaponModDefIds,
        recoilMult,
        zoom: inv.weaponZoom,
        // Aggregated: a drum that seats slower must take longer in the hands.
        reloadSec: eff.reloadSec,
        reloadTopup: def.reloadStyle === "topup",
      });
      // Aggregated: a scope's handling cost must show as a wider wander.
      this.o.aim.setWeapon({ recoil: recoilMult, ergonomics: eff.ergonomics });
    }

    // A different gun or magazine means every pending shot was charged against
    // a box no longer in the weapon. The fitted magazine is the best guess; 0
    // for an empty well.
    this.pending.length = 0;
    this.serverMag = def === null ? 0 : loadedRounds(def, inv.weaponInstance);
    this.prevServerMag = this.serverMag;
  }

  private canFire(nowMs: number): boolean {
    if (this.reloading) return false;
    if (this.mag <= 0) return false;
    // A broken gun does nothing, as `acceptFire` decides — otherwise the muzzle
    // keeps flashing for shots that fired nothing.
    if (this.isBroken()) return false;
    // An incomplete gun does nothing either. Cached in `syncWeapon`: which
    // parts
    // are fitted IS the weapon key.
    if (!this.complete) return false;
    // The rpm gate, mirroring the server's, so a shot seen fired is one
    // accepted.
    return nowMs - this.lastShotAtMs >= this.fireIntervalMs;
  }

  /**
   * The gun's condition, read live rather than cached: durability is the one
   * property that changes without `weaponKey` changing (the key ignores it so
   * a number moving every shot does not rebuild meshes). Not predicted — the
   * gun goes broken on the client up to `INV_WEAR_SYNC_INTERVAL_TICKS` late,
   * which is the correct failure.
   */
  private isBroken(): boolean {
    const def = this.o.inventory.weaponDef;
    const item = this.o.inventory.weaponInstance;
    if (def === null || item === null) return false;
    return isWeaponBroken(def, item);
  }

  /** Extra cone half-angle from wear, radians. Live for `isBroken`'s reasons. */
  private wearSpreadRad(): number {
    const def = this.o.inventory.weaponDef;
    const item = this.o.inventory.weaponInstance;
    if (def === null || item === null) return 0;
    return (wearExtraSpreadDeg(def, item) * Math.PI) / 180;
  }

  private fire(nowMs: number): void {
    this.lastShotAtMs = nowMs;
    this.pending.push(nowMs);
    // Bounded by the magazine: the list can never grow past what one mag could
    // possibly have produced, whatever the network does.
    if (this.pending.length > this.magSizeCache) this.pending.shift();

    // --- Origin: the PREDICTED eye (what the server reconciles against), not
    // the rendered one with its interpolation and smoothing.
    const state = this.o.prediction.state;
    this.eye.x = state.pos.x;
    this.eye.y = state.pos.y + eyeHeightFor(state.stance);
    this.eye.z = state.pos.z;
    // ...leaned, if peeking — not cosmetic: a shot from the unleaned eye would
    // start inside the wall. `FIRE_ORIGIN_TOLERANCE_M` absorbs the discrepancy.
    // The DISCRETE input lean, like the server's.
    applyLeanOffset(this.eye, this.o.input.yaw, this.o.input.lean);

    // --- Direction: view angles, plus hip spread ---------------------------
    this.aimDirection(this.dir);

    // --- Ticks: `tick` is the pull, `renderTick` the world the shooter was
    // LOOKING at, one interpolation delay back — what the server rewinds to.
    const synced = this.o.clock.synced;
    const tick = synced ? this.o.clock.estServerTick(nowMs) : 0;
    const renderTick = Math.max(0, Math.round(tick - INTERP_DELAY_TICKS));

    if (synced) {
      this.o.socket?.sendFire({
        tick,
        renderTick,
        origin: { x: this.eye.x, y: this.eye.y, z: this.eye.z },
        dir: { x: this.dir.x, y: this.dir.y, z: this.dir.z },
      });
    }

    // --- Local feedback, immediate. Two kicks: the viewmodel rocks the GUN,
    // the aim system moves the VIEW. Both after the direction was taken, so
    // this shot flies where the sights were and the NEXT pays for it.
    this.o.viewmodel.kick();
    this.o.aim.kick();
    this.o.fx.muzzleFlash();
    this.o.audio.localShot(this.weaponSoundId, this.suppressed);

    // The round flies from here on the client's copy of the server's
    // ballistics. `""` as shooter id: no entity has it, and self-exclusion is
    // moot.
    const eff = this.eff;
    if (eff === null) return;
    if (this.pellets <= 1) {
      this.o.bullets.spawn(this.eye, this.dir, "", true, eff);
      return;
    }
    // Buckshot: eight bolts down the cone the server rolls its own pattern in.
    // The centre direction is what was SENT.
    for (let i = 0; i < this.pellets; i++) {
      this.pellet.x = this.dir.x;
      this.pellet.y = this.dir.y;
      this.pellet.z = this.dir.z;
      // sqrt for a uniform disc — a linear roll bunches the pattern in the
      // middle and buckshot emphatically does not do that.
      this.applyCone(this.pellet, PELLET_CONE_RAD * Math.sqrt(Math.random()));
      this.o.bullets.spawn(this.eye, this.pellet, "", true, eff);
    }
  }

  /**
   * Writes the unit view direction with aim offset and hip spread. Yaw 0 looks
   * down −Z, camera is YXZ, so forward is `Ry(yaw)·Rx(pitch)·(0,0,−1)`. The
   * `aim` offset is added and clamped exactly as `loop.ts` posed the camera —
   * recoil and sway ARE where the shot goes.
   */
  private aimDirection(out: Vec3): void {
    const aim = this.o.aim;
    const yaw = this.o.input.yaw + aim.offsetYaw;
    // The zero elevation rides on the pitch, outside the clamp: a property of
    // the sight, not of where the player looks. Input clamps pitch 0.01 rad
    // short of vertical, so ~0.0007 can never push past.
    const pitch = clampPitch(this.o.input.pitch + aim.offsetPitch) + this.zeroElevationRad;
    const cp = Math.cos(pitch);

    out.x = -Math.sin(yaw) * cp;
    out.y = Math.sin(pitch);
    out.z = -Math.cos(yaw) * cp;

    // Wear is NOT faded by the sights — a loose barrel is loose whatever the
    // shooter does. Zero above `WEAR_ACCURACY_FRAC`, so a decent gun pays
    // nothing.
    const wear = this.wearSpreadRad();
    if (wear > 0) this.applyCone(out, wear * Math.sqrt(Math.random()));

    // Hip spread, faded as the sights come up. `Math.random` is right here: the
    // server trusts the direction (checking unit length) so the drawn tracer
    // and
    // the flown round are the same line.
    const spreadScale = 1 - this.o.viewmodel.adsT;
    if (spreadScale <= 0.001) return;

    // sqrt for a uniform disc: without it the cone is biased toward its centre.
    this.applyCone(out, this.hipSpreadRad * Math.sqrt(Math.random()) * spreadScale);
  }

  /**
   * Rotates unit `out` by `theta` in a uniformly random direction around
   * itself. Allocation-free.
   */
  private applyCone(out: Vec3, theta: number): void {
    if (theta <= 0) return;
    const phi = Math.random() * Math.PI * 2;

    // An orthonormal basis around `out`. Up is (0,1,0) except when looking
    // (near) straight up or down, where that degenerates and −Z is used instead.
    let ux: number;
    let uy: number;
    let uz: number;
    if (Math.abs(out.y) > 0.999) {
      ux = 0;
      uy = 0;
      uz = -1;
    } else {
      ux = 0;
      uy = 1;
      uz = 0;
    }
    // right = normalize(dir × up)
    let rx = out.y * uz - out.z * uy;
    let ry = out.z * ux - out.x * uz;
    let rz = out.x * uy - out.y * ux;
    const rl = Math.hypot(rx, ry, rz);
    if (rl < 1e-6) return;
    rx /= rl;
    ry /= rl;
    rz /= rl;
    // up' = right × dir (already unit: both operands are unit and orthogonal)
    const bx = ry * out.z - rz * out.y;
    const by = rz * out.x - rx * out.z;
    const bz = rx * out.y - ry * out.x;

    const t = Math.tan(theta);
    const ox = (rx * Math.cos(phi) + bx * Math.sin(phi)) * t;
    const oy = (ry * Math.cos(phi) + by * Math.sin(phi)) * t;
    const oz = (rz * Math.cos(phi) + bz * Math.sin(phi)) * t;

    out.x += ox;
    out.y += oy;
    out.z += oz;
    const len = Math.hypot(out.x, out.y, out.z);
    if (len > 0) {
      out.x /= len;
      out.y /= len;
      out.z /= len;
    }
  }
}

/** Confirms an authoritative hit or kill. Kept here so all three markers, and
 *  the sounds that go with them, are described in one place. */
export function confirmHit(
  audio: GameAudio,
  kind: Exclude<HitMarkerKind, "predicted">,
  nowMs: number,
): void {
  pulseHitMarker(kind, nowMs);
  if (kind === "kill") audio.deathSting();
  else audio.hitConfirm();
}
